import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { Link } from 'react-router-dom';
import { GRAY_COLORS } from '../globalTheme';
import { theme } from './theme';
import './SidebarSection.css';

interface SectionItem {
	name: string;
	link: string;
}

interface SidebarSectionProps {
	icon: React.ReactNode;
	title: string;
	items: SectionItem[];
	collapsed: boolean;
	currentPath: string;
	maxHeight?: string;
	hasDropdown?: boolean;
	isOpen?: boolean;
	onToggle?: () => void;
	isFixed?: boolean;
}

const SidebarSection: React.FC<SidebarSectionProps> = ({
	icon,
	title,
	items,
	collapsed,
	currentPath,
	maxHeight,
	hasDropdown = false,
	isOpen,
	onToggle,
	isFixed = false
}) => {
	const [hoveredItem, setHoveredItem] = useState('');

	useEffect(() => {
		if (collapsed && onToggle) onToggle();
	}, [collapsed]);

	const setToggleButtonIcon = () => {
		return isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />;
	};

	const handleSectionClick = () => {
		if (onToggle) onToggle();
	};

	const componentStyles = () => {
		const baseStyles = theme.sidebarSection;
		const openStyles = isOpen ? theme.sectionOpen : {};
		return {
			...baseStyles,
			...openStyles
		};
	};

	const isItemActive = (link: string) => currentPath.includes(link);

	const itemColor = (title: string) => {
		if (hoveredItem === title) return 'brand';
		return isFixed ? 'default' : 'subtitle';
	};

	const changeItemColorHover = (name: string) => {
		setHoveredItem(name);
	};

	if (items.length === 0) {
		return null;
	}

	return (
		<ul>
			{hasDropdown ? (
				<>
					<section
						style={componentStyles()}
						className={`sidebar-section ${collapsed ? 'collapsed-sidebar-section' : ''}`}
						onClick={handleSectionClick}
					>
						{(isFixed || collapsed) && <span>{icon}</span>}
						{!collapsed && (
							<>
								<span className="section-text">
									<Typography size="sm" color={GRAY_COLORS.gray900} weight="semibold">
										{title}
									</Typography>
								</span>
								{setToggleButtonIcon()}
							</>
						)}
					</section>
					<div
						style={{
							maxHeight: isOpen ? maxHeight : 0,
							overflowY: 'auto',
							cursor: 'pointer'
						}}
						className="custom-scrollbar scroll-shadows"
					>
						{isOpen &&
							items.map((item, index) => (
								<Link to={item.link} key={index}>
									<li
										style={{
											...theme.sectionItem,
											...(isItemActive(item.link) ? theme.activeItem : {})
										}}
										className={`section-item ${isItemActive(item.link) ? 'active' : ''}`}
										onMouseEnter={() => changeItemColorHover(item.name)}
										onMouseLeave={() => {
											setHoveredItem('');
										}}
									>
										<Typography
											size="sm"
											color={itemColor(item.name)}
											weight={isFixed ? 'semibold' : 'regular'}
										>
											{item.name}
										</Typography>
									</li>
								</Link>
							))}
					</div>
				</>
			) : (
				<Link to={items[0].link}>
					<li
						style={{
							...componentStyles(),
							...(isItemActive(items[0].link) ? theme.activeItem : {})
						}}
						className={`sidebar-section ${collapsed ? 'collapsed-sidebar-section' : ''} ${isItemActive(items[0].link) ? 'active' : ''}`}
						onMouseEnter={() => changeItemColorHover(title)}
						onMouseLeave={() => {
							setHoveredItem('');
						}}
					>
						{(isFixed || collapsed) && <span>{icon}</span>}
						{!collapsed && (
							<Typography
								size="sm"
								color={itemColor(title)}
								weight={isFixed ? 'semibold' : 'regular'}
							>
								{title}
							</Typography>
						)}
					</li>
				</Link>
			)}
		</ul>
	);
};

export default SidebarSection;
